/** @file placeholderTiles: a grey box for every unpainted tile of the preview, so empty parts can be picked. */
import type { CellRect, ResolvedEntry } from '@zilo/pose3d-export/types/bundle';
import { blitImage } from '@zilo/pose3d-image-io/image/blitImage';
import { createImage } from '@zilo/pose3d-image-io/image/createImage';
import { fillRect } from '@zilo/pose3d-image-io/image/fillRect';
import { packShelves } from '@zilo/pose3d-image-io/pack/packShelves';
import type { Image } from '@zilo/pose3d-image-io/types/image';
import type { LoadedBundle } from '@zilo/pose3d-runtime-babylon/bundle/loadedBundle';
import { PLACEHOLDER_COLORS } from './placeholderColors';

/** The placeholder look: a mid-grey box, a darker rim, the pivot pixel in the accent orange. */
const { fill: FILL, rim: RIM, pivot: PIVOT } = PLACEHOLDER_COLORS;

/** One unpainted tile: the entry that owns it and the tile index. */
type Empty = { entry: ResolvedEntry; tile: number };

/** Every entry's unpainted tiles, grouped by the sheet the entry draws from. */
function emptiesBySheet(loaded: LoadedBundle): Map<string, Empty[]> {
  const out = new Map<string, Empty[]>();
  for (const d of loaded.puppet.drawables) {
    if (d.kind !== 'impostor') continue;
    for (const entry of d.facets) {
      entry.rects.forEach((rect, tile) => {
        if (rect !== null) return;
        const list = out.get(entry.sheet) ?? [];
        list.push({ entry, tile });
        out.set(entry.sheet, list);
      });
    }
  }
  return out;
}

/** Draws one placeholder cell of the entry's size at `(x, y)`: fill, 1 px rim, the pivot pixel. */
function paintCell(sheet: Image, x: number, y: number, entry: ResolvedEntry): void {
  const [w, h] = entry.size;
  fillRect(sheet, x, y, w, h, RIM);
  if (w > 2 && h > 2) fillRect(sheet, x + 1, y + 1, w - 2, h - 2, FILL);
  const px = Math.min(w - 1, Math.max(0, Math.floor(entry.pivotPx[0])));
  const py = Math.min(h - 1, Math.max(0, Math.floor(entry.pivotPx[1])));
  fillRect(sheet, x + px, y + py, 1, 1, PIVOT);
}

/** The old sheet grown below by the packed cells: the old pixels at the top, each cell painted and its rect set. */
function grownSheet(old: Image | null, empties: Empty[]): Image {
  const packed = packShelves(empties.map((e, i) => ({ id: String(i), w: e.entry.size[0], h: e.entry.size[1] })), { maxWidth: Math.max(old?.width ?? 0, 64) });
  const top = old?.height ?? 0;
  const sheet = createImage(Math.max(old?.width ?? 0, packed.width), top + packed.height);
  if (old !== null) blitImage(sheet, old, 0, 0);
  for (const r of packed.rects) {
    const e = empties[Number(r.id)];
    if (e === undefined) continue;
    paintCell(sheet, r.x, top + r.y, e.entry);
    const rect: CellRect = [r.x, top + r.y, r.w, r.h];
    e.entry.rects[e.tile] = rect;
  }
  return sheet;
}

/** Replaces sheet `name` with its grown version (appended to the bundle when it had no such sheet) and updates its size. */
function extendSheet(loaded: LoadedBundle, name: string, empties: Empty[]): void {
  const index = loaded.sheetNames.indexOf(name);
  const sheet = grownSheet(index < 0 ? null : (loaded.sheets[index] ?? null), empties);
  loaded.puppet.sheets[name] = { file: loaded.puppet.sheets[name]?.file ?? `sheets/${name}.png`, size: [sheet.width, sheet.height] };
  if (index < 0) {
    loaded.sheetNames.push(name);
    loaded.sheets.push(sheet);
    return;
  }
  loaded.sheets[index] = sheet;
}

/**
 * Gives every unpainted tile a placeholder cell on its entry's sheet (appended below the painted cells; a sheet is
 * created when the bundle has none), so the viewport draws every part, Quads and the handles have a quad to show,
 * and a double-click or drop lands on the part. The document is untouched: Art and the Tile screen read a slot's
 * state from it, not from the bundle. A bundle with no empty tile is returned as it is.
 */
export function placeholderTiles(loaded: LoadedBundle): LoadedBundle {
  const groups = emptiesBySheet(loaded);
  if (groups.size === 0) return loaded;
  // A new puppet object: the runtime caches its view of a bundle puppet per object, so the grown sheet list must not
  // be seen through the old one.
  const puppet = { ...loaded.puppet, sheets: { ...loaded.puppet.sheets } };
  const out: LoadedBundle = { ...loaded, puppet, bundle: { ...loaded.bundle, puppet }, sheetNames: [...loaded.sheetNames], sheets: [...loaded.sheets] };
  for (const [name, empties] of groups) extendSheet(out, name, empties);
  return out;
}
